import React from 'react';
import { Pencil } from 'lucide-react';
import { CostSourceChip } from '../../../components/CostSourceChip';
import type { CostBasis } from '../../contexts/ComponentCardContext';
import type { ReplacementQuantityDisplay } from '../../../../../Helpers/maintenanceCostUtils';

interface ReplacementRowProps {
    lifetimeYears: string;
    yearsRemaining: number | null;
    /** Shown instead of the years when given (Massredigering: "Blandade") */
    yearsRemainingLabel?: string;
    /** Read-only Mängd; null without a lifetime */
    quantity: ReplacementQuantityDisplay | null;
    displayCost: string;
    costExplanation: string;
    costBasis: CostBasis;
    isHighlighted: boolean;
    isOverdue: boolean;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onOpenReplacementCostModal: () => void;
    onOpenConditionModal: () => void;
    disabled?: boolean;
}

/**
 * Replacement activity row component
 * Displays and allows editing of component replacement details
 */
export const ReplacementRow: React.FC<ReplacementRowProps> = ({
    lifetimeYears,
    yearsRemaining,
    yearsRemainingLabel,
    quantity,
    displayCost,
    costExplanation,
    costBasis,
    isHighlighted,
    isOverdue,
    onMouseEnter,
    onMouseLeave,
    onOpenReplacementCostModal,
    onOpenConditionModal,
    disabled = false
}) => {
    return (
        <div
            className={`activity-row replacement-row ${isHighlighted ? 'highlighted' : ''} ${isOverdue ? 'overdue' : ''}`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="activity-name">
                <span>Ersätt komponent</span>
            </div>

            <div className="activity-interval">
                <span className={disabled ? 'text-muted' : ''}>{lifetimeYears} år</span>
            </div>

            {/* Quantity driving the replacement cost (read-only; muted when a kostnad enligt offert overrides it) */}
            <div className="activity-area">
                <span className={disabled || !quantity || quantity.muted ? 'text-muted' : ''} title={quantity?.title}>
                    {quantity?.text ?? '–'}
                </span>
            </div>

            <div className="activity-year">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span className={disabled ? 'text-muted' : ''}>
                        {yearsRemainingLabel ?? (yearsRemaining !== null ? `${Math.max(0, Math.floor(yearsRemaining))} år kvar` : '–')}
                    </span>
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={onOpenConditionModal}
                        disabled={disabled}
                        title="Ändra skick"
                        style={{ lineHeight: 1 }}
                    >
                        <Pencil size={14} className={disabled ? 'text-muted' : 'text-primary'} />
                    </button>
                </div>
            </div>

            <div className="activity-cost">
                <div className="cost-display-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span className={`cost-value-wrapper ${disabled ? 'text-muted' : ''}`}>
                        <span className="cost-value" style={{ cursor: 'help' }}>
                            <CostSourceChip source={costBasis.source} hasAttachments={costBasis.hasAttachments} muted={disabled}>
                                {displayCost ? `${parseInt(displayCost).toLocaleString('sv-SE')} kr` : '0 kr'}
                            </CostSourceChip>
                        </span>
                        <span className="cost-tooltip">{costExplanation}</span>
                    </span>
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={onOpenReplacementCostModal}
                        disabled={disabled}
                        title="Redigera ersättningskostnad"
                        style={{ lineHeight: 1 }}
                    >
                        <Pencil size={14} className={disabled ? 'text-muted' : 'text-primary'} />
                    </button>
                </div>
            </div>
        </div>
    );
};
